import { useState } from 'react';
import { ProcessStatus, adminAPI } from '../../services/admin';
import { ConfirmActionModal } from './ConfirmActionModal';
import { useTranslation } from 'react-i18next';

interface ProcessCardProps {
  process: ProcessStatus;
  onRefresh?: () => void;
  requiresConfirmation?: boolean;
  confirmPhrase?: string;
}

export function ProcessCard({
  process,
  onRefresh,
  requiresConfirmation = false,
  confirmPhrase,
}: ProcessCardProps) {
  const [isLoading, setIsLoading] = useState(false);
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const { t } = useTranslation();

  const isRunning = process.status === 'running';
  const isFailed = process.status === 'failed';

  const executeTrigger = async () => {
    setIsLoading(true);
    try {
      await adminAPI.triggerProcess(process.processType);
      onRefresh?.();
    } catch (err) {
      alert(err instanceof Error ? err.message : t('processCard.startError'));
    } finally {
      setIsLoading(false);
      setShowConfirmModal(false);
    }
  };

  const handleTrigger = async () => {
    if (requiresConfirmation && confirmPhrase) {
      setShowConfirmModal(true);
    } else {
      if (!confirm(t('processCard.confirmStart', { name: process.displayName }))) {
        return;
      }
      await executeTrigger();
    }
  };

  const handleStop = async () => {
    if (!confirm(t('processCard.confirmStop', { name: process.displayName }))) {
      return;
    }

    setIsLoading(true);
    try {
      await adminAPI.stopProcess(process.processType);
      onRefresh?.();
    } catch (err) {
      alert(err instanceof Error ? err.message : t('processCard.stopError'));
    } finally {
      setIsLoading(false);
    }
  };

  const formatTimeAgo = (dateStr: string | null): string => {
    if (!dateStr) return '-';
    const date = new Date(dateStr);
    const now = new Date();
    const diffMs = now.getTime() - date.getTime();
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMs / 3600000);
    const diffDays = Math.floor(diffMs / 86400000);

    if (diffMins < 1) return t('processCard.justNow');
    if (diffMins < 60) return t('processCard.minsAgo', { count: diffMins });
    if (diffHours < 24) return t('processCard.hoursAgo', { count: diffHours });
    return t('processCard.daysAgo', { count: diffDays });
  };

  const getStatusColor = () => {
    switch (process.status) {
      case 'running':
        return 'bg-blue-500';
      case 'completed':
        return 'bg-green-500';
      case 'failed':
        return 'bg-red-500';
      case 'stopped':
        return 'bg-yellow-500';
      default:
        return 'bg-slate-400';
    }
  };

  const getStatusText = () => {
    switch (process.status) {
      case 'running':
        return t('processCard.status.running');
      case 'completed':
        return t('processCard.status.completed');
      case 'failed':
        return t('processCard.status.failed');
      case 'stopped':
        return t('processCard.status.stopped');
      default:
        return t('processCard.status.pending');
    }
  };

  return (
    <>
      <div className="bg-white dark:bg-slate-800 rounded-xl border border-slate-200 dark:border-slate-700 p-4">
        {/* Header */}
        <div className="flex items-start justify-between mb-3">
          <div>
            <h3 className="font-semibold text-slate-900 dark:text-white text-sm">
              {process.displayName}
            </h3>
            <p className="text-xs text-slate-500 dark:text-slate-400 mt-0.5">
              {process.description}
            </p>
          </div>
          {process.requiresDocker && (
            <span className="text-xs bg-blue-100 dark:bg-blue-900/30 text-blue-700 dark:text-blue-300 px-2 py-0.5 rounded">
              Docker
            </span>
          )}
        </div>

        {/* Status */}
        <div className="flex items-center gap-2 mb-3">
          <div className={`w-2.5 h-2.5 rounded-full ${getStatusColor()} ${isRunning ? 'animate-pulse' : ''}`} />
          <span className="text-sm font-medium text-slate-700 dark:text-slate-300">
            {getStatusText()}
          </span>
        </div>

        {/* Progress (when running) */}
        {isRunning && process.currentRun?.progress && (
          <div className="mb-3">
            <div className="flex justify-between text-xs text-slate-500 dark:text-slate-400 mb-1">
              <span>
                {process.currentRun.progress.current.toLocaleString()} / {process.currentRun.progress.total.toLocaleString()}
              </span>
              <span>{process.currentRun.progress.percent.toFixed(1)}%</span>
            </div>
            <div className="h-1.5 bg-slate-200 dark:bg-slate-700 rounded-full overflow-hidden">
              <div
                className="h-full bg-blue-500 rounded-full transition-all duration-300"
                style={{ width: `${Math.min(process.currentRun.progress.percent, 100)}%` }}
              />
            </div>
            {process.currentRun.progress.message && (
              <p className="text-xs text-slate-500 dark:text-slate-400 mt-1 truncate">
                {process.currentRun.progress.message}
              </p>
            )}
          </div>
        )}

        {/* Last run info */}
        {!isRunning && process.lastCompletedRun && (
          <div className="text-xs text-slate-500 dark:text-slate-400 mb-3">
            <span>{t('processCard.lastRun')} </span>
            <span className={isFailed ? 'text-red-500' : ''}>
              {formatTimeAgo(process.lastCompletedRun.completedAt)}
            </span>
            {process.lastCompletedRun.result?.processed !== undefined && (
              <span className="ml-1">
                {t('processCard.processed', { count: process.lastCompletedRun.result.processed as number })}
              </span>
            )}
          </div>
        )}

        {/* Error message */}
        {isFailed && process.lastCompletedRun?.errorMessage && (
          <div className="text-xs text-red-500 dark:text-red-400 mb-3 truncate" title={process.lastCompletedRun.errorMessage}>
            {process.lastCompletedRun.errorMessage}
          </div>
        )}

        {/* Actions */}
        <div className="flex gap-2">
          {isRunning ? (
            <button
              onClick={handleStop}
              disabled={isLoading}
              className="flex-1 px-3 py-1.5 text-xs font-medium bg-red-100 dark:bg-red-900/30 text-red-700 dark:text-red-300 rounded-lg hover:bg-red-200 dark:hover:bg-red-900/50 disabled:opacity-50 transition-colors"
            >
              {isLoading ? t('processCard.stopping') : t('processCard.stop')}
            </button>
          ) : (
            <button
              onClick={handleTrigger}
              disabled={isLoading}
              className="flex-1 px-3 py-1.5 text-xs font-medium bg-blue-100 dark:bg-blue-900/30 text-blue-700 dark:text-blue-300 rounded-lg hover:bg-blue-200 dark:hover:bg-blue-900/50 disabled:opacity-50 transition-colors"
            >
              {isLoading ? t('processCard.starting') : t('processCard.start')}
            </button>
          )}
        </div>
      </div>

      {/* Confirmation Modal */}
      {requiresConfirmation && confirmPhrase && (
        <ConfirmActionModal
          isOpen={showConfirmModal}
          onClose={() => setShowConfirmModal(false)}
          onConfirm={executeTrigger}
          title={t('processCard.confirmModal.title', { name: process.displayName })}
          description={t('processCard.confirmModal.description')}
          confirmPhrase={confirmPhrase}
          isLoading={isLoading}
        />
      )}
    </>
  );
}
